import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Language, SearchRecord } from '@sourcr/shared';
import { ACTIVE_SOURCES } from '@sourcr/shared';
import { I18N } from '../../lib/i18n';
import { listSearches, deleteSearch, getStats, runSearch } from '../../lib/api';
import type { Stats } from '../../lib/api';
import { SearchStatusBadge } from '../../components/ui/status-badge';
import { IconPlus } from '../../components/icons';
import { formatDate } from '../../lib/utils';
import { Skeleton } from '../../components/ui/skeleton';

interface DashboardPageProps {
  lang: Language;
}

const S = {
  page: { display: 'flex', flexDirection: 'column' as const, gap: 24 },
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 12,
  },
  greeting: { fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', margin: '4px 0 0', letterSpacing: '-0.02em' },
  greetingSub: { fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0' },
  newBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 'var(--r-md)',
    border: 'none',
    backgroundColor: 'var(--accent)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    flexShrink: 0,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 14,
  },
  statCard: {
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-lg)',
    padding: '18px 20px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
  },
  statLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    color: 'var(--text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
  },
  statValue: { fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' as const },
  heroRow: {
    display: 'grid',
    gridTemplateColumns: '1.4fr 1fr',
    gap: 16,
  },
  section: {
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-lg)',
    overflow: 'hidden',
  },
  ctaCard: {
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-lg)',
    padding: '28px 24px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 18,
    backgroundImage: 'radial-gradient(ellipse at 100% 0%, color-mix(in oklch, var(--accent) 14%, transparent), transparent 70%)',
  },
  ctaTitle: { fontSize: 19, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--text-primary)', margin: 0 },
  ctaBody: { fontSize: 13, color: 'var(--text-muted)', margin: 0, lineHeight: 1.55 },
  ctaBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '9px 16px',
    borderRadius: 'var(--r-md)',
    border: 'none',
    backgroundColor: 'var(--accent)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    width: 'fit-content' as const,
  },
  sourceBadgeRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 5,
  },
  sourcePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '3px 8px',
    borderRadius: 999,
    backgroundColor: 'var(--bg-overlay)',
    border: '1px solid var(--border)',
    fontSize: 11,
    color: 'var(--text-muted)',
    fontFamily: 'var(--font-mono)',
  },
  sectionHeader: {
    padding: '14px 20px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: 0 },
  filterBar: {
    padding: '8px 16px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    backgroundColor: 'var(--bg-elevated)',
    flexWrap: 'wrap' as const,
  },
  filterSelect: {
    outline: 'none',
    border: '1px solid var(--border)',
    borderRadius: 6,
    padding: '4px 8px',
    backgroundColor: 'var(--bg-base)',
    color: 'var(--text-primary)',
    fontSize: 12,
    cursor: 'pointer',
  },
  emptyFilter: {
    padding: '28px 16px',
    textAlign: 'center' as const,
    fontSize: 13,
    color: 'var(--text-muted)',
  },
  tableHead: {
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 2fr) 110px 100px 100px',
    padding: '9px 16px',
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    color: 'var(--text-muted)',
    borderBottom: '1px solid var(--border)',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 2fr) 110px 100px 100px',
    padding: '13px 16px',
    alignItems: 'center',
    fontSize: 13,
    borderBottom: '1px solid var(--border)',
    cursor: 'default',
  },
  searchTitle: { fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 2 },
  searchMeta: { fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' },
  viewLink: {
    color: 'var(--accent)',
    cursor: 'pointer',
    textDecoration: 'none',
    fontSize: 12,
    fontWeight: 500,
    background: 'none',
    border: 'none',
    padding: 0,
  },
  deleteBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    fontSize: 12,
    padding: '3px 6px',
    borderRadius: 4,
    marginLeft: 6,
  },
  confirmRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: 'var(--text-muted)',
  },
  confirmYesBtn: {
    padding: '3px 10px',
    borderRadius: 5,
    border: 'none',
    backgroundColor: 'var(--danger, #e53e3e)',
    color: '#fff',
    fontSize: 11,
    cursor: 'pointer',
  },
  confirmNoBtn: {
    padding: '3px 10px',
    borderRadius: 5,
    border: '1px solid var(--border)',
    background: 'none',
    color: 'var(--text-secondary)',
    fontSize: 11,
    cursor: 'pointer',
  },
  actionsCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  activityCard: {
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-lg)',
    padding: '20px 24px',
  },
  activityRow: {
    display: 'grid',
    gridTemplateColumns: '28px 1fr auto',
    gap: 12,
    padding: '10px 0',
    alignItems: 'start',
    fontSize: 13,
    borderTop: '1px dashed var(--border)',
  },
  activityDot: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: 'var(--accent-subtle)',
    color: 'var(--accent)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
  },
  activityWhen: {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--text-muted)',
    whiteSpace: 'nowrap' as const,
  },
  welcomeCard: {
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-xl)',
    padding: '56px 32px',
    textAlign: 'center' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    // Centered + capped so the hero doesn't strand dead space on wide screens.
    width: '100%',
    maxWidth: 880,
    margin: '0 auto',
    minHeight: '60vh',
    justifyContent: 'center',
    backgroundImage: 'radial-gradient(ellipse at 50% 0%, color-mix(in oklch, var(--accent) 10%, transparent), transparent 60%)',
  },
  welcomeStartBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 20px',
    borderRadius: 'var(--r-md)',
    border: 'none',
    backgroundColor: 'var(--accent)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  stepsRow: {
    display: 'flex',
    gap: 12,
    marginTop: 24,
    flexWrap: 'wrap' as const,
    justifyContent: 'center',
  },
  stepCard: {
    flex: '1 1 140px',
    maxWidth: 180,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 6,
    padding: '16px 12px',
    backgroundColor: 'var(--bg-elevated)',
    borderRadius: 'var(--r-md)',
    border: '1px solid var(--border)',
  },
};

// Honest onboarding steps — copy comes from i18n (no "7 platforms" overclaim).
const ONBOARDING_STEPS = [
  { icon: '🔍', titleKey: 'onbDescribeTitle', descKey: 'onbDescribeDesc' },
  { icon: '⚡', titleKey: 'onbSourcesTitle', descKey: 'onbSourcesDesc' },
  { icon: '⭐', titleKey: 'onbTrackTitle', descKey: 'onbTrackDesc' },
] as const;

const POLL_INTERVAL = 5_000;

export function DashboardPage({ lang }: DashboardPageProps) {
  const i = I18N[lang].dashboard;
  const navigate = useNavigate();

  const [searches, setSearches] = useState<SearchRecord[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'running' | 'completed' | 'failed'>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | '7d' | '30d'>('all');
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Client-side filtered list — stat cards still reflect the full dataset.
  const filteredSearches = useMemo(() => {
    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;
    const windowMs = dateFilter === 'today' ? DAY : dateFilter === '7d' ? 7 * DAY : dateFilter === '30d' ? 30 * DAY : null;
    return searches.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (windowMs !== null) {
        const ts = new Date(s.createdAt).getTime();
        if (Number.isNaN(ts) || now - ts > windowMs) return false;
      }
      return true;
    });
  }, [searches, statusFilter, dateFilter]);

  const hasRunning = searches.some((s) => s.status === 'running');

  // Load data on mount
  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const [srResult, statsResult] = await Promise.allSettled([
          listSearches(),
          getStats(),
        ]);
        if (!mounted) return;
        if (srResult.status === 'fulfilled') setSearches(srResult.value.searches);
        if (statsResult.status === 'fulfilled') setStats(statsResult.value);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Poll running searches every 5s until all are done
  useEffect(() => {
    if (!hasRunning) return;

    const poll = async () => {
      try {
        const [srResult, statsResult] = await Promise.allSettled([
          listSearches(),
          getStats(),
        ]);
        if (srResult.status === 'fulfilled') setSearches(srResult.value.searches);
        if (statsResult.status === 'fulfilled') setStats(statsResult.value);
      } catch {
        // ignore
      }
    };

    pollTimerRef.current = setTimeout(function tick() {
      void poll().then(() => {
        if (searches.some((s) => s.status === 'running')) {
          pollTimerRef.current = setTimeout(tick, POLL_INTERVAL);
        }
      });
    }, POLL_INTERVAL);

    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [hasRunning, searches]);

  const [runningId, setRunningId] = useState<string | null>(null);
  async function handleRun(id: string) {
    setRunningId(id);
    try {
      await runSearch(id);
      navigate(`/chat/${id}`); // chat polls running → completed, then offers results
    } catch {
      setRunningId(null);
    }
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    setConfirmDeleteId(null);
    try {
      await deleteSearch(id);
      setSearches((ss) => ss.filter((s) => s.id !== id));
      // Refresh stats after delete
      try {
        const updatedStats = await getStats();
        setStats(updatedStats);
      } catch { /* ignore */ }
    } catch {
      // silently ignore — user can retry
    } finally {
      setDeletingId(null);
    }
  }

  const showWelcome = !loading && searches.length === 0;

  const statCards = stats
    ? [
        { label: i.totalSearches, value: stats.totalSearches },
        { label: i.candidatesFound, value: stats.totalCandidates },
        { label: i.favorites, value: stats.totalFavorites },
        { label: i.contacted, value: stats.contacted },
      ]
    : [
        { label: i.totalSearches, value: searches.length },
        { label: i.candidatesFound, value: searches.filter((s) => s.status === 'completed').length },
        { label: i.favorites, value: 0 },
        { label: i.contacted, value: 0 },
      ];

  const dateLine = new Date().toLocaleDateString(lang === 'uk' ? 'uk-UA' : 'en-US', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, textTransform: 'uppercase' as const, letterSpacing: '0.1em', color: 'var(--text-muted)' }}>{dateLine}</div>
          <h1 style={S.greeting}>{i.title}</h1>
        </div>
        {!showWelcome && !loading && (
          <button type="button" onClick={() => navigate('/chat')} style={S.newBtn}>
            <IconPlus size={14} />
            {i.newSearch}
          </button>
        )}
      </div>

      {loading ? (
        <>
          <div style={S.statsGrid}>
            {[0, 1, 2, 3].map((n) => (
              <div key={n} style={S.statCard}>
                <Skeleton width="60%" height={10} style={{ marginBottom: 8 }} />
                <Skeleton width="40%" height={28} />
              </div>
            ))}
          </div>
          <div style={S.section}>
            <div style={S.sectionHeader}>
              <Skeleton width={120} height={14} />
            </div>
            <div style={{ padding: '20px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[0, 1, 2].map((n) => (
                <Skeleton key={n} width={`${70 + n * 10}%`} height={16} />
              ))}
            </div>
          </div>
        </>
      ) : showWelcome ? (
        <div style={S.welcomeCard}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>👋</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
            {lang === 'uk' ? 'Ласкаво просимо до Sourcr' : 'Welcome to Sourcr'}
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 20px', lineHeight: 1.6, maxWidth: 520 }}>
            {i.welcomeSubtitle}
          </p>
          <button type="button" onClick={() => navigate('/chat')} style={S.welcomeStartBtn}>
            {lang === 'uk' ? 'Запустити перший пошук →' : 'Start your first search →'}
          </button>
          <div style={S.stepsRow}>
            {ONBOARDING_STEPS.map((step) => (
              <div key={step.titleKey} style={S.stepCard}>
                <span style={{ fontSize: 24 }}>{step.icon}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{i[step.titleKey]}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' as const }}>{i[step.descKey]}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div style={S.statsGrid}>
            {statCards.map((s) => (
              <div key={s.label} style={S.statCard}>
                <span style={S.statLabel}>{s.label}</span>
                <span style={S.statValue}>{s.value}</span>
              </div>
            ))}
          </div>

          {/* Hero row: searches table + CTA card */}
          <div style={S.heroRow}>
            {/* Searches table */}
            <div style={S.section}>
              <div style={S.sectionHeader}>
                <h2 style={S.sectionTitle}>{i.recentSearches}</h2>
                <button type="button" style={S.viewLink} onClick={() => navigate('/chat')}>
                  {i.newSearch} →
                </button>
              </div>
              <div style={S.filterBar}>
                <select
                  style={S.filterSelect}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                  aria-label={i.filterStatus}
                >
                  <option value="all">{i.statusAll}</option>
                  <option value="running">{i.status.running}</option>
                  <option value="completed">{i.status.completed}</option>
                  <option value="failed">{i.status.failed}</option>
                </select>
                <select
                  style={S.filterSelect}
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value as typeof dateFilter)}
                  aria-label={i.filterDate}
                >
                  <option value="all">{i.dateAll}</option>
                  <option value="today">{i.dateToday}</option>
                  <option value="7d">{i.date7d}</option>
                  <option value="30d">{i.date30d}</option>
                </select>
              </div>
              <div style={S.tableHead}>
                <div>{lang === 'uk' ? 'Пошук' : 'Search'}</div>
                <div>{lang === 'uk' ? 'Статус' : 'Status'}</div>
                <div>{lang === 'uk' ? 'Дата' : 'Date'}</div>
                <div />
              </div>
              {filteredSearches.length === 0 ? (
                <div style={S.emptyFilter}>{i.noFilterMatch}</div>
              ) : filteredSearches.map((search) => (
                <div key={search.id} style={S.tableRow}>
                  <div>
                    <div style={S.searchTitle}>{search.title}</div>
                    <div style={S.searchMeta}>{search.criteria.skills.slice(0, 3).join(' · ')}</div>
                  </div>
                  <div><SearchStatusBadge status={search.status} lang={lang} /></div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>
                    {formatDate(search.createdAt, lang)}
                  </div>
                  <div style={S.actionsCell}>
                    {confirmDeleteId === search.id ? (
                      <div style={S.confirmRow}>
                        <button type="button" style={S.confirmYesBtn} disabled={deletingId === search.id} onClick={() => void handleDelete(search.id)}>
                          {i.confirmYes}
                        </button>
                        <button type="button" style={S.confirmNoBtn} onClick={() => setConfirmDeleteId(null)}>
                          {i.confirmNo}
                        </button>
                      </div>
                    ) : (
                      <>
                        {search.status === 'completed' && (
                          <button type="button" style={S.viewLink} onClick={() => navigate(`/results/${search.id}`)}>
                            {lang === 'uk' ? 'Результати →' : 'View →'}
                          </button>
                        )}
                        {search.status === 'ready' && (
                          <button type="button" style={S.viewLink} disabled={runningId === search.id} onClick={() => void handleRun(search.id)}>
                            {runningId === search.id ? (lang === 'uk' ? 'Запуск…' : 'Starting…') : (lang === 'uk' ? 'Запустити →' : 'Run →')}
                          </button>
                        )}
                        {(search.status === 'pending' || search.status === 'running') && (
                          <button type="button" style={S.viewLink} onClick={() => navigate(`/chat/${search.id}`)}>
                            {lang === 'uk' ? 'Продовжити →' : 'Continue →'}
                          </button>
                        )}
                        {search.status === 'failed' && (
                          <button type="button" style={S.viewLink} onClick={() => navigate(`/chat/${search.id}`)}>
                            {lang === 'uk' ? 'Повторити →' : 'Retry →'}
                          </button>
                        )}
                        <button type="button" style={S.deleteBtn} title={i.deleteSearch} onClick={() => setConfirmDeleteId(search.id)} disabled={deletingId === search.id}>
                          ✕
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA card */}
            <div style={S.ctaCard}>
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, textTransform: 'uppercase' as const, letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: 8 }}>
                  {lang === 'uk' ? 'Почніть тут' : 'Start here'}
                </div>
                <h2 style={S.ctaTitle}>
                  {lang === 'uk'
                    ? 'Знайдіть наступного Senior Backend за 3 повідомлення.'
                    : 'Hire your next senior backend in 3 prompts.'}
                </h2>
                <p style={{ ...S.ctaBody, marginTop: 8 }}>
                  {i.welcomeSubtitle}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" style={S.ctaBtn} onClick={() => navigate('/chat')}>
                  {lang === 'uk' ? 'Відкрити чат' : 'Open agent chat'} →
                </button>
              </div>
              <div style={S.sourceBadgeRow}>
                {ACTIVE_SOURCES.map((s) => (
                  <span key={s.key} style={S.sourcePill}>{s.label}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Activity feed */}
          <div style={S.activityCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                {i.activityFeed}
              </span>
            </div>
            {searches.slice(0, 4).map((search, idx) => (
              <div key={search.id} style={{ ...S.activityRow, borderTop: idx === 0 ? 'none' : '1px dashed var(--border)' }}>
                <div style={S.activityDot}>
                  {search.status === 'completed' ? '✓' : search.status === 'running' ? '⟳' : search.status === 'failed' ? '!' : '·'}
                </div>
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{search.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {search.criteria.skills.slice(0, 3).join(' · ')}
                  </div>
                </div>
                <div style={S.activityWhen}>{formatDate(search.createdAt, lang)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
